/**
 * Screen 1e — one course's report.
 *
 * One request. Every panel is derived from the same resolved learner set
 * (assignees ∪ enrolments) filtered by the same team and period, and eight
 * requests would resolve it eight times and could disagree — a completion rate
 * that does not match the table under it is the kind of wrong that costs a
 * customer's trust in the whole screen.
 *
 * The team and period filters are part of the request, so changing one is a new
 * load rather than a filter applied here. That is also what makes **Export CSV**
 * honest: the link carries the same query, so the file is what is on screen.
 *
 * **Open in Compose** from the design is not built — Compose registers no URL
 * scheme. It is in docs/BACKLOG.md rather than a button that does nothing.
 */

import { useState } from 'react';
import type { ReportLessonWire, ReportWire } from '@wire';
import { errorText, teach, type ReportFilter } from '../api';
import { ErrorNote, Footnote, Loading, OkNote, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { StatRow } from '../components/Bits';
import { CertificateList } from '../components/Certificates';
import { ds } from '../ds';
import { useRouter } from '../router';
import {
  HAIR,
  RADIUS,
  figure,
  formatDeadline,
  meta,
  monoPlain,
  plural,
  secondsLabel,
  tableHead
} from '../theme';
import { useLoad } from '../useLoad';

const DAY = 86400;

const PERIODS: { value: string; label: string; days: number | null }[] = [
  { value: 'all', label: 'All time', days: null },
  { value: '30', label: 'Last 30 days', days: 30 },
  { value: '90', label: 'Last 90 days', days: 90 },
  { value: '365', label: 'Last 12 months', days: 365 }
];

/** How many rows of the learners table are drawn before **Show all**. */
const FIRST_PAGE = 25;

export default function Report({ id }: { id: string }) {
  const { Badge, Button, Select, Stat } = ds();
  const { navigate } = useRouter();

  const [team, setTeam] = useState('');
  const [period, setPeriod] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [responses, setResponses] = useState(false);
  const [nudged, setNudged] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const days = PERIODS.find((p) => p.value === period)?.days ?? null;
  const filter: ReportFilter = {
    ...(team ? { team } : {}),
    ...(days ? { from: Math.floor(Date.now() / 1000) - days * DAY } : {})
  };

  const { data, error, loading } = useLoad(
    () => teach.report(id, filter),
    'Could not load the report.',
    `${id}|${team}|${period}`
  );

  // Read only while the panel is open: **All responses** decrypts every
  // answer on the server, and a report is opened far more often than it is
  // read through.
  const all = useLoad(
    () => (responses ? teach.feedbackForm.responses(id) : Promise.resolve(null)),
    'Could not load the responses.',
    responses ? `${id}|open` : 'closed'
  );

  const certificates = useLoad(
    () => teach.courseCertificates(id),
    'Could not load the certificates.',
    id
  );

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return loading ? <Loading /> : null;

  const { course, stats, deadline, lessons, questions, feedback, learners } = data;
  const rows = showAll ? learners : learners.slice(0, FIRST_PAGE);

  const nudge = async () => {
    setBusy(true);
    setFailed(null);
    try {
      const result = await teach.nudge(id, { userIds: null });
      setNudged(`Nudged ${plural(result.nudged, 'person', 'people')}.`);
    } catch (err) {
      setFailed(errorText(err, 'Could not send the nudge.'));
    }
    setBusy(false);
  };

  return (
    <>
      <PageTitle title={course.title} meta={meta(course.code, `pass mark ${data.passMark}%`)}>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/teach/courses/${id}/feedback`)}>
          Feedback form
        </Button>
        {/* A real link, not a fetch: the browser's own download beats reading a
            multi-megabyte CSV into memory to hand it back as a blob — which is
            also why this cannot be a DS `Button` wrapped in an anchor. A
            `download` link is only a download when it is the element the
            click actually lands on, so it is hand-styled to match one instead. */}
        <a
          href={teach.reportCsvUrl(id, filter)}
          download
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '7px 14px',
            borderRadius: RADIUS.control,
            border: HAIR,
            background: 'var(--surface-card)',
            color: 'var(--text-strong)',
            fontSize: 13.5,
            fontWeight: 600,
            textDecoration: 'none'
          }}
        >
          Export CSV
        </a>
      </PageTitle>

      <ErrorNote>{failed}</ErrorNote>
      <OkNote>{nudged}</OkNote>

      <Panel style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Select
          label="Team"
          size="sm"
          value={team}
          style={{ minWidth: 190 }}
          onChange={(e) => setTeam(e.target.value)}
          options={[
            { value: '', label: 'Everyone' },
            ...data.teams.map((t) => ({ value: t.id, label: `${t.name} · ${t.members}` }))
          ]}
        />
        <Select
          label="Period"
          size="sm"
          value={period}
          style={{ minWidth: 170 }}
          onChange={(e) => setPeriod(e.target.value)}
          options={PERIODS.map((p) => ({ value: p.value, label: p.label }))}
        />
      </Panel>

      <Panel>
        <StatRow>
          <Stat label="Learners" value={stats.learners.toLocaleString()} />
          <Stat label="Completed" value={stats.completed.toLocaleString()} />
          <Stat
            label="Average score"
            value={stats.averageScore === null ? '—' : `${stats.averageScore}%`}
          />
          <Stat
            label="Average time"
            value={stats.averageSeconds === null ? '—' : secondsLabel(stats.averageSeconds)}
          />
          <Stat
            label="Feedback"
            value={stats.feedbackMean === null ? '—' : stats.feedbackMean.toFixed(1)}
          />
        </StatRow>
      </Panel>

      <Panel style={{ display: 'grid', gap: 16 }}>
        <PanelHead title="Completions over time" />
        <Completions points={data.completionsOverTime} />
      </Panel>

      <Panel style={{ display: 'grid', gap: 16 }}>
        <PanelHead title="Against the deadline">
          {deadline.unfinishedPast > 0 && (
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void nudge()}>
              {`Nudge the ${deadline.unfinishedPast}`}
            </Button>
          )}
        </PanelHead>
        <StatRow>
          <Stat label="On time" value={deadline.onTime.toLocaleString()} />
          <Stat label="Late" value={deadline.late.toLocaleString()} />
          <Stat label="Past deadline, unfinished" value={deadline.unfinishedPast.toLocaleString()} />
        </StatRow>
      </Panel>

      <Panel style={{ display: 'grid', gap: 14 }}>
        <PanelHead title="Where learners stop" meta="Reached each lesson" />
        {lessons.map((lesson) => (
          <LessonBar key={lesson.lessonId} lesson={lesson} />
        ))}
        {lessons.length === 0 && <Footnote>Nothing has been uploaded yet.</Footnote>}
      </Panel>

      <Panel style={{ display: 'grid', gap: 14 }}>
        <PanelHead title="Time spent per lesson" meta="Median against the estimate" />
        {lessons.map((lesson) => {
          const worst = Math.max(
            1,
            ...lessons.map((l) => Math.max(l.medianSeconds ?? 0, l.plannedSeconds))
          );
          return (
            <div key={lesson.lessonId} style={{ display: 'grid', gap: 5 }}>
              <span style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>
                {`${lesson.index + 1}. ${lesson.title}`}
              </span>
              <Track
                value={((lesson.medianSeconds ?? 0) / worst) * 100}
                tone="var(--signal)"
                label={lesson.medianSeconds === null ? 'No one yet' : secondsLabel(lesson.medianSeconds)}
              />
              <Track
                value={(lesson.plannedSeconds / worst) * 100}
                tone="var(--border-strong)"
                label={`${secondsLabel(lesson.plannedSeconds)} planned`}
              />
            </div>
          );
        })}
      </Panel>

      <Panel style={{ display: 'grid', gap: 14 }}>
        <PanelHead title="Questions people get wrong" meta={`Pass mark ${data.passMark}%`} />
        {questions.length === 0 ? (
          <Footnote>No question has been answered yet.</Footnote>
        ) : (
          questions.map((q) => (
            <div key={q.blockId} style={{ display: 'grid', gap: 5, borderTop: HAIR, paddingTop: 12 }}>
              <span style={monoPlain}>{`Lesson ${q.lessonIndex + 1}`}</span>
              <span style={{ fontSize: 14, color: 'var(--text-strong)' }}>{q.prompt}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Track
                  value={q.correctPct}
                  tone={q.correctPct < data.passMark ? 'var(--danger-500)' : 'var(--success-500)'}
                  label={`${q.correctPct}% right first time`}
                />
                <span style={{ ...monoPlain, ...figure }}>{plural(q.attempted, 'answer')}</span>
              </div>
            </div>
          ))
        )}
      </Panel>

      <Panel style={{ display: 'grid', gap: 16 }}>
        <PanelHead
          title="Feedback"
          meta={feedback.count === 0 ? null : plural(feedback.count, 'response')}
        >
          {feedback.count > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setResponses((v) => !v)}>
              {responses ? 'Hide responses' : 'All responses'}
            </Button>
          )}
        </PanelHead>

        {feedback.count === 0 ? (
          <Footnote>Nobody has answered yet.</Footnote>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
              <span style={{ ...figure, fontSize: 34, fontWeight: 800, color: 'var(--text-strong)' }}>
                {feedback.mean === null ? '—' : feedback.mean.toFixed(1)}
              </span>
              <div style={{ display: 'grid', gap: 4, flex: 1, minWidth: 220 }}>
                {[5, 4, 3, 2, 1].map((score) => {
                  const count = feedback.histogram[score - 1] ?? 0;
                  const top = Math.max(1, ...feedback.histogram);
                  return (
                    <div key={score} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <span style={{ ...monoPlain, width: 12 }}>{score}</span>
                      <Track value={(count / top) * 100} tone="var(--primary)" label={String(count)} />
                    </div>
                  );
                })}
              </div>
            </div>

            {feedback.quotes.map((quote, i) => (
              <blockquote
                key={`${quote.name ?? 'anon'}-${i}`}
                style={{
                  margin: 0,
                  paddingLeft: 14,
                  borderLeft: '3px solid var(--primary-soft)',
                  display: 'grid',
                  gap: 4
                }}
              >
                <span style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--text-body)' }}>
                  {quote.text}
                </span>
                <span style={monoPlain}>
                  {meta(quote.name, quote.team, quote.rating === null ? null : `${quote.rating} of 5`)}
                </span>
              </blockquote>
            ))}

            {responses && (
              <div style={{ display: 'grid', gap: 12, borderTop: HAIR, paddingTop: 14 }}>
                <ErrorNote>{all.error}</ErrorNote>
                {all.data?.responses.map((r) => (
                  <div key={r.id} style={{ display: 'grid', gap: 5 }}>
                    <span style={monoPlain}>
                      {meta(r.name ?? 'Anonymous', r.team, r.rating === null ? null : `${r.rating} of 5`)}
                    </span>
                    {r.answers.map((a) => (
                      <span
                        key={a.questionId}
                        style={{ fontSize: 13.5, color: 'var(--text-body)' }}
                      >
                        {a.value}
                      </span>
                    ))}
                  </div>
                ))}
                {all.data?.responses.length === 0 && <Footnote>Nothing to show.</Footnote>}
              </div>
            )}
          </>
        )}
      </Panel>

      <Panel style={{ display: 'grid', gap: 12 }}>
        <PanelHead title="Learners" meta={plural(learners.length, 'learner')} />

        <div className="mv-scroll-x">
          <table style={{ width: '100%', minWidth: 780, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Name', 'Email', 'Team', 'Progress', 'Score', 'Time', 'Deadline'].map((h) => (
                  <th key={h} style={{ ...tableHead, textAlign: 'left', padding: '0 10px 8px 0' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.userId} style={{ borderTop: HAIR }}>
                  <td style={{ padding: '10px 10px 10px 0', fontSize: 13.5 }}>
                    {l.name}
                    {!l.member && <span style={{ ...monoPlain, marginLeft: 8 }}>No longer a member</span>}
                    {l.renewal && (
                      <span style={{ marginLeft: 8 }}>
                        <Badge tone={l.renewal === 'expired' ? 'danger' : 'warning'} mono>
                          {l.renewal === 'expired' ? 'Expired' : 'Due again'}
                        </Badge>
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 10px 10px 0', ...monoPlain }}>{l.email ?? '—'}</td>
                  <td style={{ padding: '10px 10px 10px 0', fontSize: 13 }}>{l.team ?? '—'}</td>
                  <td style={{ padding: '10px 10px 10px 0', ...monoPlain, ...figure }}>{`${l.percent}%`}</td>
                  <td style={{ padding: '10px 10px 10px 0', ...monoPlain, ...figure }}>
                    {l.score === null ? '—' : `${l.score}%`}
                  </td>
                  <td style={{ padding: '10px 10px 10px 0', ...monoPlain, ...figure }}>
                    {l.seconds > 0 ? secondsLabel(l.seconds) : '—'}
                  </td>
                  <td style={{ padding: '10px 0' }}>
                    {l.completedAt === null && l.dueAt !== null && l.dueAt < Date.now() / 1000 ? (
                      <Badge tone="danger" mono>
                        Past deadline
                      </Badge>
                    ) : l.late === true ? (
                      <Badge tone="warning" mono>
                        Late
                      </Badge>
                    ) : l.late === false ? (
                      <Badge tone="success" mono>
                        On time
                      </Badge>
                    ) : (
                      <span style={monoPlain}>
                        {l.dueAt === null ? 'No deadline' : formatDeadline(l.dueAt)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {learners.length > FIRST_PAGE && !showAll && (
          <div>
            <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
              {`Show all ${learners.length}`}
            </Button>
          </div>
        )}
      </Panel>

      {certificates.data && certificates.data.certificates.length > 0 && (
        <Panel style={{ display: 'grid', gap: 4 }}>
          <PanelHead
            title="Certificates"
            meta={plural(certificates.data.certificates.length, 'certificate')}
          />
          <CertificateList certificates={certificates.data.certificates} showLearner />
        </Panel>
      )}
    </>
  );
}

/** One horizontal bar with its number beside it. */
function Track({ value, tone, label }: { value: number; tone: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          height: 8,
          borderRadius: 999,
          background: 'var(--surface-sunken)',
          overflow: 'hidden'
        }}
      >
        <span
          style={{
            display: 'block',
            width: `${Math.max(0, Math.min(100, value))}%`,
            height: '100%',
            borderRadius: 999,
            background: tone
          }}
        />
      </span>
      <span style={{ ...monoPlain, ...figure, minWidth: 118, textAlign: 'right' }}>{label}</span>
    </div>
  );
}

function LessonBar({ lesson }: { lesson: ReportLessonWire }) {
  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <span style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>
        {`${lesson.index + 1}. ${lesson.title}`}
      </span>
      <Track value={lesson.reachedPct} tone="var(--primary)" label={`${lesson.reachedPct}% reached`} />
    </div>
  );
}

/**
 * Completions over time.
 *
 * An inline SVG rather than a charting library: this is one cumulative series
 * with no interaction, and the smallest chart library in the suite would be
 * larger than the whole page.
 */
function Completions({ points }: { points: ReportWire['completionsOverTime'] }) {
  if (points.length === 0) return <Footnote>Nobody has finished yet.</Footnote>;

  const top = Math.max(1, ...points.map((p) => p.cumulative));
  const width = 640;
  const height = 140;
  const step = points.length > 1 ? width / (points.length - 1) : 0;

  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(height - (p.cumulative / top) * height).toFixed(1)}`)
    .join(' ');

  const first = points[0]!;
  const last = points[points.length - 1]!;

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${last.cumulative} completions by ${formatDeadline(last.day)}`}
        style={{ width: '100%', height: 140, overflow: 'visible' }}
      >
        <path
          d={`${path} L ${width} ${height} L 0 ${height} Z`}
          fill="var(--primary-soft)"
          stroke="none"
        />
        <path d={path} fill="none" stroke="var(--primary)" strokeWidth={2} vectorEffect="non-scaling-stroke" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', ...monoPlain }}>
        <span>{formatDeadline(first.day)}</span>
        <span>{`${last.cumulative} finished`}</span>
        <span>{formatDeadline(last.day)}</span>
      </div>
    </div>
  );
}
